import { useState } from "react";

import { Button } from "@virtual-brain-lib/frontend-design-system/ui";
import type { ChatMessage } from "@virtual-brain-lib/types";

import { useChatContext } from "../../core/context/ChatContext";
import { INITIAL_MESSAGE_ID } from "../../core/utils/chatbotInitialMessage";
import MessageSuggestedFollowUpCard from "./MessageSuggestedFollowUpCard";

export default function MessageSuggestedFollowUps({
	message,
	isLastMessage,
	onFollowUp,
}: {
	readonly message: ChatMessage;
	readonly isLastMessage: boolean;
	readonly onFollowUp: (followUp: string) => Promise<void>;
}) {
	const isInitialMessage = message.id === INITIAL_MESSAGE_ID;
	const options = useChatContext();
	const [currentPromptIndex, setCurrentPromptIndex] = useState(0);

	// Display initial suggested prompts
	// - if the displaySuggestedPrompts option is enabled
	// - or if the message is the initial message, regardless of the option
	const hasFollowUps =
		(options.displaySuggestedPrompts || isInitialMessage) &&
		message.suggestedPrompts &&
		message.suggestedPrompts.length > 0 &&
		isLastMessage;

	if (!hasFollowUps) {
		return null;
	}

	const promptCount = message.suggestedPrompts!.length;

	return (
		<div className="my-4 flex h-[60px] w-full items-center gap-1 px-2">
			<Button
				leftIcon="chevronLeft"
				variant="ghost"
				onClick={() => {
					setCurrentPromptIndex((prev) => (prev === 0 ? promptCount - 1 : prev - 1));
				}}
			/>
			<div className="min-w-0 flex-1 overflow-hidden">
				<div
					className="flex transition-transform duration-300"
					style={{ transform: `translateX(-${currentPromptIndex * 100}%)` }}
				>
					{message.suggestedPrompts!.map((prompt, index) => (
						<div key={index} className="flex h-full w-full shrink-0 px-2">
							<MessageSuggestedFollowUpCard
								text={prompt}
								onClick={() => {
									if (onFollowUp) {
										onFollowUp(prompt);
									}
								}}
							/>
						</div>
					))}
				</div>
			</div>
			<Button
				leftIcon="chevronRight"
				variant="ghost"
				onClick={() => {
					setCurrentPromptIndex((prev) => (prev === promptCount - 1 ? 0 : prev + 1));
				}}
			/>
		</div>
	);
}
